// The timeline itself: header column on the left, and on the right the lanes (silence, camera, picture, then two lanes
// per character) drawn inside a native horizontal scroller. Pure drawing; the editor owns scrolling and selection.
import { memo, type CSSProperties, type ReactNode } from "react";
import { imageSrc } from "../../library/images";
import { useLibrary } from "../../library/store";
import { findAsset } from "../../model/library";
import { castOf, displayDuration, faceAt, isVoiceOver, linesOf, marksOf, motionOf, resolve, type TimelineEpisode } from "../../model/timeline";
import { Avatar } from "../common";

export const PX = 56; // pixels per second
export type Sel = { kind: "camera" | "picture" | "feeling" | "line"; char?: string; id: string };
const same = (a: Sel | null, kind: Sel["kind"], id: string) => !!a && a.kind === kind && a.id === id;

const FRAMING = { whole: "Whole", medium: "Medium", close: "Close-up", free: "Your frame" };
const hasSilence = (ep: TimelineEpisode) => ep.lines.some(l => l.how === "silent");
const fmtTick = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const Heads = memo(function Heads({ ep }: { ep: TimelineEpisode }) {
  const lib = useLibrary();
  return (
    <div className="tl-heads">
      <div className="tl-ruler-space" />
      <div className="tl-head r-pic">Pictures</div>
      {castOf(ep).map(c => (
        <div key={c} className="tl-char">
          <Avatar assetId={c} size={24} />
          <span>{findAsset(lib, c)?.name ?? "?"}</span>
        </div>
      ))}
      {hasSilence(ep) && <div className="tl-head r-silence">Silence</div>}
      <div className="tl-head r-cam">Camera</div>
    </div>
  );
});

type ClipProps = { x: number; w: number; sel: boolean; onClick: () => void; className: string; children?: ReactNode; style?: CSSProperties; title?: string };
function Clip({ x, w, sel, onClick, className, children, style, title }: ClipProps) {
  return (
    <div className={`clip ${className}${sel ? " sel" : ""}`} title={title} style={{ left: x, width: Math.max(w, 10), ...style }}
      onClick={e => { e.stopPropagation(); onClick(); }}>
      <span>{children}</span>
    </div>
  );
}

// The parts of [from, to) where the character's face is (or is not) on screen, so feelings can dim off screen.
// Who is on screen only changes where a picture starts.
function pieces(ep: TimelineEpisode, char: string, from: number, to: number) {
  const r = resolve(ep);
  const cuts = [from, to, ...r.pictures.map(p => p.t)].filter(x => x >= from && x <= to).sort((a, b) => a - b);
  const out: { a: number; b: number; on: boolean }[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const a = cuts[i]!, b = cuts[i + 1]!;
    if (b - a > 1e-6) out.push({ a, b, on: !!faceAt(ep, char, (a + b) / 2) });
  }
  return out;
}

// onAddAt: a tap on a default block (what applies before the first marker) opens the add page for that kind there.
export const Lanes = memo(function Lanes({ ep, half, sel, onSel, onAddAt }: {
  ep: TimelineEpisode; half: number; sel: Sel | null; onSel: (s: Sel) => void; onAddAt: (kind: "camera" | "feeling", t: number, char?: string) => void;
}) {
  const dur = displayDuration(ep);
  const X = (t: number) => half + t * PX;
  const ticks: number[] = [];
  for (let s = 0; s <= dur; s += 2) ticks.push(s);
  const r = resolve(ep);
  const pictures = marksOf(ep, "picture");
  const picEnd = (i: number) => pictures[i + 1]?.t ?? dur;
  const cams = marksOf(ep, "camera");
  const camDefaultEnd = cams[0] ? cams[0].t : dur; // the whole picture shows until the first camera marker
  const secs = (n: number) => `${Math.round(n * 10) / 10}s`;
  return (
    <div className="tl-inner" style={{ width: dur * PX + half * 2 }}>
      <div className="tl-ruler">{ticks.map(s => <span key={s} style={{ left: X(s) }}>{fmtTick(s)}</span>)}</div>
      {pictures.map(p => <i key={p.id} className="tl-cut" style={{ left: X(p.t) }} />)}

      <div className="tl-row r-pic">
        {pictures.map((p, i) => (
          <Clip key={p.id} className={`pic${motionOf(p) ? " anim" : ""}`} x={X(p.t)} w={(picEnd(i) - p.t) * PX} sel={same(sel, "picture", p.id)} onClick={() => onSel({ kind: "picture", id: p.id })}
            style={{ backgroundImage: `url("${imageSrc(p.picture.src)}")` }} />
        ))}
      </div>

      {castOf(ep).map(c => (
        <div key={c} className="tl-char-lanes">
          <div className="tl-row r-feel">
            {(() => {
              // Before their first feeling marker a character is neutral. Drawn from 0 like any feeling block, dimmed where they are off screen.
              const firstFeel = r.feelings.find(k => k.char === c);
              const to = firstFeel ? firstFeel.t : dur;
              return pieces(ep, c, 0, to).map((p, i) => (
                <Clip key={"d" + i} className={`feel default${p.on ? "" : " off"}`} x={X(p.a)} w={(p.b - p.a) * PX} sel={false} title="Default: tap to set an expression here"
                  onClick={() => onAddAt("feeling", p.a, c)}>{i === 0 ? "neutral" : ""}</Clip>
              ));
            })()}
            {r.feelings.filter(k => k.char === c).flatMap(k => pieces(ep, c, k.t, k.end).map((p, i) => (
              <Clip key={k.id + i} className={`feel${p.on ? "" : " off"}`} x={X(p.a)} w={(p.b - p.a) * PX} sel={same(sel, "feeling", k.id)} onClick={() => onSel({ kind: "feeling", char: c, id: k.id })}>
                {i === 0 ? k.emotion : ""}
              </Clip>
            )))}
          </div>
          <div className="tl-row r-voice">
            {linesOf(ep, c).map(l => (
              <Clip key={l.id} className={`line ${l.status}${isVoiceOver(ep, l) ? " vo" : ""}`} x={X(l.start)} w={l.duration * PX}
                sel={same(sel, "line", l.id)} onClick={() => onSel({ kind: "line", char: c, id: l.id })} title={l.error}>
                {l.status === "error" ? "! " : ""}{l.text ?? (l.how === "recorded" ? "Recording" : "")}
              </Clip>
            ))}
          </div>
        </div>
      ))}
      {hasSilence(ep) && <div className="tl-row r-silence">
        {r.lines.map(l => l.how === "silent" && (
          <Clip key={l.id} className="silence" title="Silence" x={X(l.start)} w={l.duration * PX} sel={same(sel, "line", l.id)} onClick={() => onSel({ kind: "line", id: l.id })}>{secs(l.duration)}</Clip>
        ))}
      </div>}
      <div className="tl-row r-cam">
        {ep.pictures.length > 0 && camDefaultEnd > 1e-6 && (
          <Clip className="cam default" x={X(0)} w={camDefaultEnd * PX} sel={false} title="Default: tap to set the camera here" onClick={() => onAddAt("camera", 0)}>{FRAMING.whole}</Clip>
        )}
        {cams.map(k => (
          <Clip key={k.id} className="cam" x={X(k.t)} w={(k.end - k.t) * PX} sel={same(sel, "camera", k.id)} onClick={() => onSel({ kind: "camera", id: k.id })}>
            {FRAMING[k.framing]}
          </Clip>
        ))}
      </div>
    </div>
  );
});
